import { create } from 'zustand';
import type { SurveyTemplate, SurveyField, SurveyResponse } from '../types';
import { uid } from '../utils/helpers';
import { toast } from 'sonner';

const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

// ── Default fields every trainer gets ──────

export function createDefaultSurveyFields(): SurveyField[] {
  return [
    {
      id: uid(),
      type: 'text',
      label: 'Imię i nazwisko',
      placeholder: 'Jan Kowalski',
      required: true,
    },
    {
      id: uid(),
      type: 'number',
      label: 'Wiek',
      placeholder: '25',
      required: true,
      min: 12,
      max: 99,
    },
    {
      id: uid(),
      type: 'select',
      label: 'Płeć',
      required: true,
      options: ['Mężczyzna', 'Kobieta', 'Inna'],
    },
    {
      id: uid(),
      type: 'number',
      label: 'Wzrost (cm)',
      placeholder: '175',
      required: true,
      min: 100,
      max: 250,
    },
    {
      id: uid(),
      type: 'number',
      label: 'Waga (kg)',
      placeholder: '75',
      required: true,
      min: 30,
      max: 300,
    },
    {
      id: uid(),
      type: 'radio',
      label: 'Doświadczenie treningowe',
      required: true,
      options: [
        'Początkujący (< 1 rok)',
        'Średniozaawansowany (1–3 lata)',
        'Zaawansowany (3–7 lat)',
        'Ekspert (7+ lat)',
      ],
    },
    {
      id: uid(),
      type: 'multiselect',
      label: 'Cel treningowy',
      required: true,
      options: [
        'Budowa masy mięśniowej',
        'Siła',
        'Redukcja tkanki tłuszczowej',
        'Ogólna sprawność',
        'Wytrzymałość',
        'Poprawa zdrowia',
      ],
    },
    {
      id: uid(),
      type: 'multiselect',
      label: 'Dostępne dni treningowe',
      required: true,
      options: [
        'Poniedziałek',
        'Wtorek',
        'Środa',
        'Czwartek',
        'Piątek',
        'Sobota',
        'Niedziela',
      ],
    },
    {
      id: uid(),
      type: 'range',
      label: 'Ile czasu na jedną sesję treningową (minuty)',
      required: true,
      min: 30,
      max: 150,
      step: 15,
      defaultValue: '60',
    },
    {
      id: uid(),
      type: 'multiselect',
      label: 'Dostępny sprzęt (który chcesz używać)',
      required: true,
      options: [
        'Siłownia pełna (komercyjna)',
        'Siłownia domowa (podstawowa)',
        'Tylko sztanga i obciążenie',
        'Hantle',
        'Kettlebell',
        'Gumy oporowe',
        'Maszyny',
        'Wyciąg',
        'Brak - ćwiczenia z masą ciała',
      ],
    },
    {
      id: uid(),
      type: 'multiselect',
      label: 'Sprzęt którego chcesz unikać',
      required: false,
      options: [
        'Sztanga',
        'Hantle',
        'Kettlebell',
        'Gumy oporowe',
        'Maszyny',
        'Wyciąg/kabel',
        'Żaden - używam wszystkiego',
      ],
    },
    {
      id: uid(),
      type: 'textarea',
      label: 'Kontuzje / ograniczenia zdrowotne',
      placeholder: 'Opisz wszelkie kontuzje, bóle, ograniczenia ruchomości...',
      required: false,
    },
    {
      id: uid(),
      type: 'textarea',
      label: 'Obecny plan treningowy / aktywność',
      placeholder: 'Opisz co robisz aktualnie (np. 3x w tygodniu siłownia, bieganie 2x)...',
      required: false,
    },
    {
      id: uid(),
      type: 'textarea',
      label: 'Ulubione / nielubiane ćwiczenia',
      placeholder: 'Napisz ćwiczenia które lubisz lub których chcesz unikać...',
      required: false,
    },
    {
      id: uid(),
      type: 'textarea',
      label: 'Dodatkowe uwagi',
      placeholder: 'Cokolwiek dodatkowego co trener powinien wiedzieć...',
      required: false,
    },
  ];
}

// ── Store ──────────────────────────────────

interface SurveyState {
  surveys: SurveyTemplate[];
  responses: SurveyResponse[];
  loading: boolean;
  error: string | null;

  // API
  fetchSurveys: () => Promise<void>;
  createSurveyAPI: (title: string, description?: string) => Promise<void>;
  updateSurveyAPI: (surveyId: string, updates: Partial<SurveyTemplate>) => Promise<void>;
  deleteSurveyAPI: (surveyId: string) => Promise<void>;
  fetchResponsesAPI: (surveyId: string) => Promise<void>;
  submitResponseAPI: (surveyId: string, data: any, publicToken?: string) => Promise<void>;

  // Survey CRUD (local)
  createSurvey: (trainerId: string, title?: string) => SurveyTemplate;
  updateSurvey: (surveyId: string, updates: Partial<SurveyTemplate>) => void;
  deleteSurvey: (surveyId: string) => void;

  // Field ops
  addField: (surveyId: string, field: SurveyField) => void;
  updateField: (surveyId: string, fieldId: string, updates: Partial<SurveyField>) => void;
  removeField: (surveyId: string, fieldId: string) => void;
  reorderFields: (surveyId: string, fromIndex: number, toIndex: number) => void;

  // Responses
  submitResponse: (response: Omit<SurveyResponse, 'id' | 'submittedAt'> & { publicToken?: string }) => Promise<void>;
  deleteResponse: (responseId: string) => void;

  // Getters
  getSurveysByTrainer: (trainerId: string) => SurveyTemplate[];
  getResponsesBySurvey: (surveyId: string) => SurveyResponse[];
  getSurveyById: (surveyId: string) => SurveyTemplate | undefined;
}

export const useSurveyStore = create<SurveyState>()((set, get) => ({
  surveys: [],
  responses: [],
  loading: false,
  error: null,

  // ── API Functions ──
  fetchResponsesAPI: async (surveyId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys/${surveyId}/responses`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch responses');
      const data = await response.json();
      set((s) => ({
        responses: [
          ...s.responses.filter((r) => r.surveyId !== surveyId),
          ...(data.responses || []).map((r: any) => ({
            ...r,
            id: r._id || r.id,
            submittedAt: r.createdAt || r.submittedAt,
            // Convert DB array format [{fieldId, value}] → client record {fieldId: value}
            answers: Array.isArray(r.answers)
              ? r.answers.reduce((acc: Record<string, any>, a: any) => {
                if (a.fieldId != null) acc[a.fieldId] = a.value;
                return acc;
              }, {})
              : r.answers || {},
          })),
        ],
      }));
    } catch (error) {
      toast.error('Błąd pobierania odpowiedzi');
      console.error('Fetch responses error:', error);
    }
  },

  submitResponseAPI: async (surveyId: string, data: any, publicToken?: string) => {
    try {
      // Use provided publicToken, look up from store, or fall back to surveyId itself
      const token = publicToken ?? get().surveys.find((s) => s.id === surveyId)?.publicToken ?? surveyId;
      if (!token) throw new Error('Brak publicznego tokena ankiety');
      const response = await fetch(`${API_BASE_URL}/surveys/public/${token}/responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Błąd wysyłania odpowiedzi');
      toast.success('Odpowiedź zapisana!');
    } catch (error) {
      toast.error('Błąd wysyłania odpowiedzi');
      console.error('Submit response error:', error);
    }
  },
  fetchSurveys: async () => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/surveys`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch surveys');
      const data = await response.json();
      const surveys = (data.surveys || []).map((s: any) => ({
        ...s,
        id: s._id || s.id,
        trainerId: s.ownerId,
        publicToken: s.publicToken,
        responseCount: s.responseCount ?? 0,
      }));
      set({ surveys, loading: false });
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to fetch surveys';
      set({ loading: false, error: msg });
      console.error('Fetch surveys error:', error);
    }
  },

  createSurveyAPI: async (title: string, description?: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title,
          description: description || '',
          isPublic: true,
          fields: createDefaultSurveyFields().map(f => ({
            id: f.id,
            label: f.label,
            type: f.type,
            required: f.required,
            options: f.options,
          })),
        }),
      });
      if (!response.ok) throw new Error('Failed to create survey');
      // After creating, fetch surveys to get publicToken
      await get().fetchSurveys();
      toast.success('Ankieta utworzona!');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Błąd przy tworzeniu ankiety';
      toast.error(msg);
      console.error('Create survey error:', error);
    }
  },

  updateSurveyAPI: async (surveyId: string, updates: Partial<SurveyTemplate>) => {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys/${surveyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update survey');
      await get().fetchSurveys();
      toast.success('Ankieta zaktualizowana!');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Błąd przy aktualizacji';
      toast.error(msg);
      console.error('Update survey error:', error);
    }
  },

  deleteSurveyAPI: async (surveyId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/surveys/${surveyId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to delete survey');
      await get().fetchSurveys();
      toast.success('Ankieta usunięta!');
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Błąd przy usuwaniu';
      toast.error(msg);
      console.error('Delete survey error:', error);
    }
  },

  // ── Local Functions (for editing before save) ──

  createSurvey: (trainerId, title) => {
    const survey: SurveyTemplate = {
      id: uid(),
      trainerId,
      title: title || 'Ankieta dla klienta',
      description: 'Wypełnij ankietę aby trener mógł przygotować dla Ciebie indywidualny plan treningowy.',
      fields: createDefaultSurveyFields(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    set((s) => ({ surveys: [...s.surveys, survey] }));
    return survey;
  },

  updateSurvey: (surveyId, updates) => {
    set((s) => ({
      surveys: s.surveys.map((sv) =>
        sv.id === surveyId
          ? { ...sv, ...updates, updatedAt: new Date().toISOString() }
          : sv
      ),
    }));
  },

  deleteSurvey: (surveyId) => {
    set((s) => ({
      surveys: s.surveys.filter((sv) => sv.id !== surveyId),
      responses: s.responses.filter((r) => r.surveyId !== surveyId),
    }));
  },

  addField: (surveyId, field) => {
    set((s) => ({
      surveys: s.surveys.map((sv) =>
        sv.id === surveyId
          ? { ...sv, fields: [...sv.fields, field], updatedAt: new Date().toISOString() }
          : sv
      ),
    }));
  },

  updateField: (surveyId, fieldId, updates) => {
    set((s) => ({
      surveys: s.surveys.map((sv) =>
        sv.id === surveyId
          ? {
            ...sv,
            fields: sv.fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
            updatedAt: new Date().toISOString(),
          }
          : sv
      ),
    }));
  },

  removeField: (surveyId, fieldId) => {
    set((s) => ({
      surveys: s.surveys.map((sv) =>
        sv.id === surveyId
          ? { ...sv, fields: sv.fields.filter((f) => f.id !== fieldId), updatedAt: new Date().toISOString() }
          : sv
      ),
    }));
  },

  reorderFields: (surveyId, fromIndex, toIndex) => {
    set((s) => ({
      surveys: s.surveys.map((sv) => {
        if (sv.id !== surveyId) return sv;
        const fields = [...sv.fields];
        const [moved] = fields.splice(fromIndex, 1);
        fields.splice(toIndex, 0, moved);
        return { ...sv, fields, updatedAt: new Date().toISOString() };
      }),
    }));
  },

  submitResponse: async (response) => {
    // API-backed: send to backend
    const { surveyId, publicToken, ...rest } = response;
    await get().submitResponseAPI(surveyId, rest, publicToken);
  },

  deleteResponse: (responseId) => {
    set((s) => ({ responses: s.responses.filter((r) => r.id !== responseId) }));
  },

  getSurveysByTrainer: (trainerId) => {
    return get().surveys.filter((s) => s.trainerId === trainerId);
  },

  getResponsesBySurvey: (surveyId) => {
    return get().responses.filter((r) => r.surveyId === surveyId);
  },

  getSurveyById: (surveyId) => {
    return get().surveys.find((s) => s.id === surveyId);
  },
}));

